/**
 * Bug-tracker types — local read-model that mirrors the Asana
 * "Engineering - Master" project. Source of truth is Asana; this
 * collection is rebuilt from webhook events + periodic reconcile.
 */
/**
 * Writable Priority options, ordered most- to least-urgent for pickers. Every
 * Asana option is enabled, so this is the full set — unlike Support, nothing is
 * display-only. Single source of truth for the intake forms, the picker, and the
 * server-side zod enum; never copy the literals.
 */
export const BUG_TRACKER_PRIORITY_OPTIONS = [
    'High',
    'Medium',
    'Low',
];
/** Selectable TW multi_enum options, in Asana's option order. */
export const BUG_TRACKER_TW_OPTIONS = [
    'Update Github',
    'QRG',
    'Manual',
    'Website',
    'DMX Chart',
    'Spec',
    'Photometrics Report',
];
/**
 * Selectable Pending multi_enum options, in Asana's option order. "Pending" is
 * what Tech Writing is waiting on before a task can be finished — Asana's own
 * dependencies feature can't express it, hence a field (#575).
 */
export const BUG_TRACKER_PENDING_OPTIONS = [
    'Specs',
    'PLS',
    'Cover image',
    'Logo',
    'QR code',
    'STP',
    'Web',
];
/**
 * Tech Writing's own progress states, in Asana's option order (#574).
 *
 * Deliberately a custom field rather than status-pill options: the pill is the
 * PD Ticket custom type, shared by every engineering task, so adding these
 * there would hand TW's vocabulary to the whole board. Six of them mirror the
 * states the PD - TASK type uses on the PDLC/TechWriting projects, which is
 * what TW already works to; 'Pending' was added for TW in #708 and exists only
 * here. Note the separate BUG_TRACKER_PENDING_OPTIONS field above is a
 * different thing that happens to share the word.
 */
export const BUG_TRACKER_TW_STATUS_OPTIONS = [
    'Not Started',
    'Pending',
    'In Progress',
    'With PM',
    'Blocked',
    'Hold',
    'Completed',
    'Dropped',
];
/**
 * Selectable Support options — the *enabled* options on the Asana field, in
 * Asana's option order (verified live 2026-07-13). "Tech Writing" is intentionally
 * omitted: it is disabled in Asana and can no longer be assigned (Asana rejects a
 * write to a disabled option), though legacy tasks may still hold it — such a value
 * is still displayed, just not offered in the picker.
 */
export const BUG_TRACKER_SUPPORT_OPTIONS = [
    'Engineer Testing',
    'EU Parts - Close Ticket',
    'EU CS - Close Ticket',
    'US Parts - Close Ticket',
    'US CS - Close Ticket',
];
/**
 * Asana Custom Task Type status — the colored pill at the top-left of a task
 * (e.g. New / In progress / Pending / Closed). This is NOT a custom field: it
 * lives on the task's `custom_type_status_option`, and the selectable option set
 * is per custom TYPE. Two types are in use in the bug tracker (verified 2026-07-13);
 * see the catalog below. Read/written via `custom_type_status_option` on the task,
 * never via `custom_fields`.
 */
export const BUG_TRACKER_CUSTOM_TYPE_GIDS = {
    /** "PD Ticket" — Engineering-Master + Internal Response-Master tasks. */
    pdTicket: '1211966431133520',
    /** "PD Software" — Software-Master tasks (different, larger status set). */
    pdSoftware: '1212208492666142',
};
/**
 * How long a status Atlas wrote stays eligible to be recognised as reverted.
 *
 * The rules on these projects fire 1.7–2.5s after the human change, but the
 * webhook carrying the revert can arrive later still, so the window is generous
 * compared with the behaviour it watches for. It only has to be short enough
 * that an unrelated change made minutes later is never mistaken for a revert.
 */
export const BUG_TRACKER_STATUS_INTENT_TTL_MS = 60_000;
/**
 * Status option catalog keyed by custom-type gid. Sourced from Asana
 * `/custom_types/{gid}` and verified live 2026-07-13. Used by the backend to
 * validate writes and by the frontend to build the status picker.
 */
export const BUG_TRACKER_STATUS_OPTIONS_BY_TYPE = {
    [BUG_TRACKER_CUSTOM_TYPE_GIDS.pdTicket]: [
        { name: 'New', optionGid: '1211966431121863', color: 'blue', enabled: true },
        { name: 'In progress', optionGid: '1211966431121864', color: 'yellow', enabled: true },
        { name: 'Pending', optionGid: '1211966431121865', color: 'yellow-orange', enabled: true },
        { name: 'Closed', optionGid: '1211966431121866', color: 'green', enabled: true },
    ],
    [BUG_TRACKER_CUSTOM_TYPE_GIDS.pdSoftware]: [
        { name: 'Need Review', optionGid: '1212208492666131', color: 'blue', enabled: true },
        { name: 'In progress', optionGid: '1212208492666132', color: 'yellow', enabled: false },
        { name: 'Pending', optionGid: '1212208492666133', color: 'yellow-orange', enabled: false },
        { name: 'In Testing', optionGid: '1212207870981215', color: 'orange', enabled: true },
        { name: 'PD Final Approval', optionGid: '1212220705356339', color: 'yellow', enabled: true },
        { name: 'Upload to Github', optionGid: '1212207870981216', color: 'yellow-green', enabled: true },
        { name: 'Internal Only', optionGid: '1212220705356340', color: 'none', enabled: true },
        { name: 'On Github', optionGid: '1212208492666134', color: 'green', enabled: true },
        { name: 'Rejected', optionGid: '1212207870981217', color: 'red', enabled: true },
    ],
};
/**
 * Distinct status names across all known custom types, in a stable order — used
 * to populate status filter menus (the board toolbar / table filter) where a
 * single list must cover every type.
 */
export const BUG_TRACKER_ALL_STATUS_NAMES = Array.from(new Set(Object.values(BUG_TRACKER_STATUS_OPTIONS_BY_TYPE).flatMap((opts) => opts.map((o) => o.name))));
/** Option set for a task's custom type; empty when the type is unknown/absent. */
export function bugTrackerStatusOptionsForType(customTypeGid) {
    return (customTypeGid && BUG_TRACKER_STATUS_OPTIONS_BY_TYPE[customTypeGid]) || [];
}
/**
 * The options to offer for one task: what Asana reported on the mirror sweep
 * if we have it, else the static catalog for its type.
 *
 * The live set is preferred because it cannot go stale — status options are
 * added in the Asana UI (the API has no write route for them), so a catalog
 * literal is only ever a snapshot of what the type offered on the day it was
 * written. The catalog stays as the fallback for rows mirrored before the
 * sweep began carrying options, and for the "task has no status yet" picker,
 * where there is no task-reported set to read.
 */
export function bugTrackerStatusOptionsFor(task) {
    if (task.statusOptions?.length)
        return task.statusOptions;
    return bugTrackerStatusOptionsForType(task.status?.customTypeGid);
}
/**
 * Status names to offer in a filter, given the loaded rows: the static catalog
 * plus every option the rows' types actually report. Same reasoning as the
 * board's Product Family list — an option added to a custom type in Asana
 * becomes filterable without a release, and an option no code knows about is
 * still worth offering once a task carries it.
 */
export function bugTrackerStatusNamesFrom(tasks) {
    const names = new Set(BUG_TRACKER_ALL_STATUS_NAMES);
    for (const task of tasks) {
        for (const option of task.statusOptions ?? []) {
            if (option.enabled)
                names.add(option.name);
        }
        if (task.status?.name)
            names.add(task.status.name);
    }
    return Array.from(names);
}
/** The PD Ticket status the rules police. */
const BUG_TRACKER_CLOSED_STATUS_NAME = 'Closed';
/**
 * Which close preconditions this task does not currently meet.
 *
 * Empty for any status other than Closed — the rules only fire on a close — and
 * empty when nothing is missing. Both fields checked here are mirrored from
 * Asana, so this reads the same data the rule will.
 */
export function bugTrackerUnmetClosePreconditions(task, targetStatusName) {
    if (targetStatusName !== BUG_TRACKER_CLOSED_STATUS_NAME)
        return [];
    const unmet = [];
    if (!task.resolution?.trim())
        unmet.push('resolution');
    if (!task.assigneeGid)
        unmet.push('assignee');
    return unmet;
}
/** True when `optionGid` is a selectable (enabled) status for the given type. */
export function isSelectableBugTrackerStatus(customTypeGid, optionGid) {
    return bugTrackerStatusOptionsForType(customTypeGid).some((o) => o.optionGid === optionGid && o.enabled);
}
export const BUG_TRACKER_WAITING_STATES = [
    { filterValue: '__waiting_engineering', label: 'Waiting on Engineering', support: 'Engineer Testing' },
    { filterValue: '__waiting_tech_writing', label: 'Waiting on Tech Writing', support: 'Tech Writing' },
    { filterValue: '__waiting_eu_parts', label: 'Waiting to Close - EU Parts', support: 'EU Parts - Close Ticket' },
    { filterValue: '__waiting_eu_cs', label: 'Waiting to Close - EU CS', support: 'EU CS - Close Ticket' },
    { filterValue: '__waiting_us_parts', label: 'Waiting to Close - US Parts', support: 'US Parts - Close Ticket' },
    { filterValue: '__waiting_us_cs', label: 'Waiting to Close - US CS', support: 'US CS - Close Ticket' },
    { filterValue: '__waiting_other', label: 'Waiting on Other', support: null },
];
/** The "Other" catch-all state (Pending with no recognised Support). */
const WAITING_OTHER = BUG_TRACKER_WAITING_STATES.find((s) => s.support === null);
/**
 * The label to DISPLAY for a status pill. "Pending" is remapped to its matching
 * `BUG_TRACKER_WAITING_STATES` label based on the task's Support field; every other
 * status name is returned unchanged. This only affects what the UI shows — the
 * underlying Asana status stays "Pending".
 */
export function bugTrackerStatusDisplayLabel(statusName, support) {
    if (!statusName)
        return '';
    if (statusName !== 'Pending')
        return statusName;
    return (BUG_TRACKER_WAITING_STATES.find((s) => s.support === support) ?? WAITING_OTHER).label;
}
/** True when `value` is one of the synthetic waiting-state filter values. */
export function isBugTrackerWaitingFilterValue(value) {
    return BUG_TRACKER_WAITING_STATES.some((s) => s.filterValue === value);
}
/**
 * True when a task is "waiting" — parked in the "Pending" status pill, i.e. the
 * ball is with another team (any Support value), not with the owning PM. Drives
 * the Home split between "needs your action" and "waiting on others".
 */
export function isBugTrackerTaskWaiting(task) {
    return task.status?.name === 'Pending';
}
/**
 * What to show in a "Requested By" cell. A requester recovered from the Asana
 * intake form has an email and no name — nobody is in Asana to name — so the
 * address is the label rather than a blank. Every surface that renders the
 * requester goes through this, so table, board search, detail and export agree.
 */
export function bugRequestedByLabel(task) {
    return task.requestedByName ?? task.requestedByEmail ?? undefined;
}
/**
 * True when `task` matches the given synthetic waiting-state filter value. A task
 * is "waiting" only while its status pill is "Pending"; the Support field then
 * decides which state. "Other" matches any Pending task whose Support is none of
 * the specifically-labelled values.
 */
export function matchesBugTrackerWaitingFilter(value, task) {
    if (task.status?.name !== 'Pending')
        return false;
    const state = BUG_TRACKER_WAITING_STATES.find((s) => s.filterValue === value);
    if (!state)
        return false;
    if (state.support !== null)
        return task.support === state.support;
    const support = task.support ?? null;
    return !BUG_TRACKER_WAITING_STATES.some((s) => s.support !== null && s.support === support);
}
//# sourceMappingURL=bug-tracker.types.js.map